import { Dispatch, SetStateAction, useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { UseMutationResult, useQuery } from '@tanstack/react-query'
import { Money, Currencies } from 'ts-money'
import {
  IInvoice,
  IPaymentCalculateResponse,
  IPaymentTaxInput,
  IPaymentConceptoInput,
  TCFDIVersion,
} from 'models/invoices.models'
import { getSafeDecimalValue, getFormattedCurrency } from 'lib/helpers/shared.helpers'
import {
  selectActiveWorkspace,
  selectCatalogue,
  selectCreatePaymentComplement,
} from 'lib/helpers/selectors'
import useLoader from 'hooks/Shared/useLoader'
import useAddPaymentComplement from 'hooks/Invoices/PaymentComplements/useAddPaymentComplement'
import { useCfdi, useCfdiPaidAmount } from 'hooks/queries/invoices'
import { useAppSelector } from 'store/hooks'
import { storeFormValue } from 'actions/forms'
import useDialog from 'hooks/Shared/useDialog'
import ErrorModalIcon from 'assets/icons/ErrorModalIcon'
import {
  calculatePaymentComplement,
  buildConceptosFromCfdi,
} from 'services/Invoices/invoices.services'
import { buildTaxesFromCalculateResponse } from 'lib/helpers/paymentComplement.helpers'

interface IUseAddPaymentComplementContainerProps {
  invoice: IInvoice | null
  invoiceId: number
  version: TCFDIVersion
  cleanup: () => void
}

export interface IUseAddPaymentComplementContainer {
  valid: boolean
  addMutation: UseMutationResult<void, any, void>
  isa: Money
  ip: Money
  monto: Money
  paidAmount: Money
  invoiceTotal: Money
  isInvoiceFullyPaid: boolean
  isAmountExceedingBalance: boolean
  montoTotalPagosDisplay: number
  montoTotalPagosLabel: string
  calculatePreview: IPaymentCalculateResponse | null
  isCalculating: boolean
  saveValueToStore: (name: string, value: any) => void
  setValid: Dispatch<SetStateAction<boolean>>
}

const buildTaxInputsFromInvoice = (invoice: IInvoice): IPaymentTaxInput[] => {
  if (!invoice.invoiceTaxes?.length) return []
  return invoice.invoiceTaxes.map(t => {
    let baseOriginal = t.baseOriginal ?? 0
    if (!baseOriginal && t.factor !== 'Exento' && invoice.subtotal) {
      baseOriginal = invoice.subtotal
    }
    return {
      tipo: t.tr === 'Traslado' ? 'Traslado' : 'Retencion',
      impuesto: String(t.impuesto).padStart(3, '0'),
      tipoFactor: t.factor,
      tasaOCuota: t.factor === 'Exento' ? undefined : t.tasa,
      baseOriginal,
    } as IPaymentTaxInput
  })
}

const useAddPaymentComplementContainer = ({
  invoice,
  invoiceId,
  version,
  cleanup,
}: IUseAddPaymentComplementContainerProps): IUseAddPaymentComplementContainer => {
  const dispatch = useDispatch()
  const dialog = useDialog()
  const loader = useLoader()

  const activeWorkspace = useAppSelector(selectActiveWorkspace)
  const form = useAppSelector(selectCreatePaymentComplement)
  const monedas = useAppSelector(selectCatalogue('monedas'))

  const [valid, setValid] = useState(false)

  const isV4 = useMemo(() => {
    const v = invoice?.version || version
    return parseFloat(v as string) === 4
  }, [invoice?.version, version])

  const monto = Money.fromDecimal(
    getSafeDecimalValue(form.monto),
    Currencies.MXN
  )

  const conceptos = useMemo<IPaymentConceptoInput[]>(() => {
    if (!invoice || !isV4 || !invoice.cfdiConceptos?.length) return []
    return buildConceptosFromCfdi(invoice.cfdiConceptos)
  }, [invoice, isV4])

  const taxes = useMemo<IPaymentTaxInput[]>(() => {
    if (!invoice || !isV4 || conceptos.length > 0) return []
    return buildTaxInputsFromInvoice(invoice)
  }, [invoice, isV4, conceptos.length])

  const montoDecimal = getSafeDecimalValue(form.monto)
  const totalOriginal = getSafeDecimalValue(form.totalOriginal ?? String(invoice?.total ?? 0))

  // ===== IDENTIFICACIÓN DE LAS 3 MONEDAS =====
  const monedaDR = invoice?.currency?.value ?? 'MXN'  // Moneda de la factura
  const monedaP = form.moneda?.value ?? 'MXN'         // Moneda del pago

  // ===== MATRIZ COMPLETA DE 4 ESCENARIOS =====
  // 🟢 Escenario 1: Pesos Directo (MonedaDR == MXN && MonedaP == MXN)
  const isEscenario1PesosDirecto = monedaDR === 'MXN' && monedaP === 'MXN'
  
  // 🔵 Escenario 3: Factura Extranjera Pagada en Pesos (MonedaDR != MXN && MonedaP == MXN)
  const isEscenario3FacturaExtranjeraPagoPesos = monedaDR !== 'MXN' && monedaP === 'MXN'

  // ===== ASIGNACIÓN DE equivalenciaDR =====
  // Se toma directamente del campo tipoCambioDR ingresado por el usuario
  // Si las monedas son iguales, se asigna 1 automáticamente
  const tipoCambioDRIngresado = getSafeDecimalValue(form.tipoCambioDR)
  const equivalenciaDRNum = monedaDR === monedaP ? 1 : tipoCambioDRIngresado

  // tipoCambioP:
  // - Escenarios 1 y 3: 1 (pago en MXN)
  // - Escenarios 2 y 4: valor del campo tipoCambio
  const tipoCambioPNum = isEscenario1PesosDirecto || isEscenario3FacturaExtranjeraPagoPesos
    ? 1
    : getSafeDecimalValue(form.tipoCambio)

  const canCalculate =
    isV4 &&
    montoDecimal > 0 &&
    totalOriginal > 0 &&
    (conceptos.length > 0 || taxes.length > 0)

  const [debouncedMonto, setDebouncedMonto] = useState(montoDecimal)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedMonto(montoDecimal), 600)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [montoDecimal])

  // ===== SIN CONVERSIÓN: ENVIAR MONTO TAL CUAL =====
  // El campo montoPagado se envía exactamente como el usuario lo ingresa.
  // El backend realizará todas las conversiones necesarias usando equivalenciaDR y tipoCambioP.
  // Esto elimina errores de redondeo y mantiene precisión decimal en todas las monedas.
  const montoPagadoToSend = debouncedMonto

  const {
    data: calculatePreview,
    isFetching: isCalculating,
    isError: isCalculateError,
    refetch: refetchCalculate,
  } = useQuery<IPaymentCalculateResponse, any>(
    ['calculate-payment', activeWorkspace, invoiceId, montoPagadoToSend, totalOriginal, equivalenciaDRNum, tipoCambioPNum, conceptos.length > 0 ? conceptos : taxes],
    () => {
      return calculatePaymentComplement(
        activeWorkspace,
        invoiceId,
        montoPagadoToSend,  // ← Enviar tal cual, sin conversión
        totalOriginal,
        taxes,
        equivalenciaDRNum,
        tipoCambioPNum,
        conceptos.length > 0 ? conceptos : undefined,
      )
    },
    {
      enabled: canCalculate && debouncedMonto > 0,
      keepPreviousData: true,
      staleTime: 0,
    }
  )

  const montoTotalPagosDisplay = useMemo<number>(() => {
    if (!calculatePreview?.totales?.MontoTotalPagos) return 0
    // CRÍTICO: totales.MontoTotalPagos SIEMPRE está en MXN según el contrato del backend
    // Referencia: API-PaymentTaxCalculator-Contract.md línea 272
    return parseFloat(calculatePreview.totales.MontoTotalPagos)
  }, [calculatePreview])

  // IMPORTANTE: totales.MontoTotalPagos SIEMPRE está en MXN
  // Si el pago es en moneda extranjera, el backend ya hizo la conversión
  const montoTotalPagosLabel = monedaP !== 'MXN'
    ? 'Monto total con impuestos (MXN)'
    : 'Monto total con impuestos'

  // ===== IMPORTE PAGADO =====
  const ip = useMemo<Money>(() => {
    if (version === '3.3') {
      return Money.fromDecimal(getSafeDecimalValue(form.importePagado), Currencies.MXN)
    }
    
    // PRIORIDAD 1: Usar impPagadoDR del backend
    if (calculatePreview?.impPagadoDR !== undefined) {
      return Money.fromDecimal(calculatePreview.impPagadoDR, Currencies.MXN)
    }
    
    // PRIORIDAD 2: Usar conceptos[0].total del backend
    if (calculatePreview?.conceptos && calculatePreview.conceptos.length > 0) {
      const totalConcepto = calculatePreview.conceptos[0].total || 0
      return Money.fromDecimal(totalConcepto, Currencies.MXN)
    }
    
    // PRIORIDAD 3: Si no hay conceptos pero hay totales, usar MontoTotalPagos
    if (calculatePreview) {
      return Money.fromDecimal(montoTotalPagosDisplay, Currencies.MXN)
    }
    
    // FALLBACK: Si no hay preview y MonedaP ≠ MonedaDR, convertir para mostrar aproximación
    if (monedaP !== monedaDR) {
      const tipoCambioVal = getSafeDecimalValue(form.tipoCambio)
      if (tipoCambioVal > 0) {
        const importe = Math.round(montoDecimal * tipoCambioVal * 100) / 100
        return Money.fromDecimal(importe, Currencies.MXN)
      }
    }
    
    // FALLBACK FINAL: Usar el monto ingresado
    return monto
  }, [version, form.importePagado, calculatePreview, montoTotalPagosDisplay, monedaP, monedaDR, form.tipoCambio, montoDecimal, monto])

  const {
    isFetching: isFetchingInvoice,
    isLoading: isLoadingInvoice,
    isError: isInvoiceError,
    refetch: refetchInvoice,
  } = useCfdi(activeWorkspace, invoiceId)

  const {
    data: paidAmountData,
    isFetching: isFetchingPaidAmount,
    isLoading: isLoadingPaidAmount,
    isError: isPaidAmountError,
    refetch: refetchPaidAmount,
  } = useCfdiPaidAmount(activeWorkspace, invoice, {
    enabled: !!invoice && isV4,
  })

  const paidAmount = paidAmountData ?? Money.fromDecimal(0, Currencies.MXN)

  const invoiceTotal = Money.fromDecimal(invoice?.total || 0, Currencies.MXN)

  /**
   * La factura está totalmente liquidada cuando el monto ya pagado es mayor o igual
   * al total de la factura. Se activa solo en v4.0 donde tenemos paidAmount real.
   */
  const isInvoiceFullyPaid = useMemo(() => {
    if (!isV4 || !invoice?.total) return false
    return paidAmount.greaterThanOrEqual(invoiceTotal)
  }, [isV4, invoice?.total, paidAmount, invoiceTotal])

  /**
   * El monto que el usuario está intentando pagar (importePagado calculado del backend),
   * sumado a lo ya pagado, supera el total de la factura.
   * Usa el valor de conceptos[0].total del backend para validación precisa.
   */
  const isAmountExceedingBalance = useMemo(() => {
    if (!isV4 || !invoice?.total || ip.isZero()) return false
    return paidAmount.add(ip).greaterThan(invoiceTotal)
  }, [isV4, invoice?.total, paidAmount, ip, invoiceTotal])


  const taxesForCreate = useMemo<IPaymentTaxInput[]>(
    () => calculatePreview ? buildTaxesFromCalculateResponse(calculatePreview) : taxes,
    [calculatePreview, taxes]
  )

  const addMutation = useAddPaymentComplement({
    activeWorkspace,
    invoice,
    version,
    taxes: taxesForCreate,
    onSuccess: cleanup,
  })

 const isa = version === '3.3'
      ? Money.fromDecimal(getSafeDecimalValue(form.importeSaldoAnterior), Currencies.MXN)
      : invoiceTotal.subtract(paidAmount)

  const saveValueToStore = useCallback((name: string, value: any) => {
    dispatch(storeFormValue('createPaymentComplement', name, value))
  }, [dispatch])

  const isAnyQueryLoading = useMemo(
    () => [
      isFetchingInvoice,
      isLoadingInvoice,
      isFetchingPaidAmount,
      isLoadingPaidAmount,
    ].some(Boolean),
    [isFetchingInvoice, isLoadingInvoice, isFetchingPaidAmount, isLoadingPaidAmount]
  )

  const hasAnyQueryError = useMemo(
    () => [isInvoiceError, isPaidAmountError].some(Boolean),
    [isInvoiceError, isPaidAmountError]
  )

  useEffect(() => {
    if (!form.moneda) {
      const mxn = monedas.filter(m => m.value === 'MXN')[0]
      saveValueToStore('moneda', mxn)
    }
  }, [form.moneda, monedas, saveValueToStore])

  useEffect(() => {
    if (!form.fechaPago) {
      const today = new Date()
      const dd = String(today.getDate()).padStart(2, '0')
      const mm = String(today.getMonth() + 1).padStart(2, '0')
      const yyyy = today.getFullYear()
      saveValueToStore('fechaPago', `${dd}/${mm}/${yyyy}`)
    }
  }, [form.fechaPago, saveValueToStore])

  useEffect(() => {
    if (isV4 && invoice?.total && !form.totalOriginal) {
      saveValueToStore('totalOriginal', String(invoice.total))
    }
  }, [isV4, invoice?.total, form.totalOriginal, saveValueToStore])

  const ipDecimal = ip.toDecimal()
  useEffect(() => {
    if (isV4 && ipDecimal > 0) {
      saveValueToStore('importePagado', String(ipDecimal))
    }
  }, [isV4, ipDecimal, saveValueToStore])


  useEffect(() => {
    if (isAnyQueryLoading) {
      loader.primary('Cargando información del comprobante')
    } else {
      loader.close()
    }
  }, [isAnyQueryLoading, loader])

  useEffect(() => {
    if (hasAnyQueryError) {
      dialog.danger({
        subtitle: 'No se pudo obtener la información del CFDI.',
        primaryButtonText: 'Reintentar',
        secondaryButtonText: 'Cancelar',
        onPrimaryClick: () => {
          refetchInvoice()
          refetchPaidAmount()
        },
        onSecondaryClick: cleanup,
      })
    }
  }, [hasAnyQueryError, dialog, refetchInvoice, refetchPaidAmount, cleanup])

   /**
   * Modal de factura liquidada: se dispara una sola vez cuando los datos
   * ya cargaron y se confirma que paidAmount >= total.
   * El usuario solo puede cerrar el modal (botón «Entendido») y el formulario
   * queda bloqueado por el flag isInvoiceFullyPaid.
   */
  useEffect(() => {
    if (isInvoiceFullyPaid && !isAnyQueryLoading) {
      dialog.danger({
        icon: ErrorModalIcon,
        title: 'Factura liquidada',
        subtitle: `Esta factura ya fue liquidada en su totalidad. El monto total es ${getFormattedCurrency(invoiceTotal.toDecimal())} y ya se han registrado pagos por ${getFormattedCurrency(paidAmount.toDecimal())}. Para registrar un nuevo pago cancela primero alguno de los complementos existentes.`,
        primaryButtonText: 'Entendido',
        onPrimaryClick: cleanup,
      })
    }
  // Se omite dialog intencionalmente para no re-disparar al cambiar la referencia del hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvoiceFullyPaid, isAnyQueryLoading])

  /**
   * Modal de monto excedido: se dispara cuando el usuario ingresa un monto
   * que sumado a lo ya pagado supera el total de la factura.
   */
  useEffect(() => {
    if (isAmountExceedingBalance) {
      dialog.danger({
        icon: ErrorModalIcon,
        title: 'Monto excedido',
        subtitle: `El monto ingresado supera el saldo pendiente de la factura. El máximo permitido es ${getFormattedCurrency(invoiceTotal.subtract(paidAmount).toDecimal())}.`,
        primaryButtonText: 'Entendido',
      })
    }
  // Se omite dialog intencionalmente para no re-disparar al cambiar la referencia del hook
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAmountExceedingBalance])

  useEffect(() => {
    if (isCalculateError && canCalculate) {
      dialog.danger({
        subtitle: 'No se pudo calcular el desglose de impuestos.',
        primaryButtonText: 'Reintentar',
        secondaryButtonText: 'Cancelar',
        onPrimaryClick: () => refetchCalculate(),
        onSecondaryClick: cleanup,
      })
    }
  }, [isCalculateError, canCalculate, dialog, refetchCalculate, cleanup])

  return {
    valid,
    addMutation,
    isa,
    ip,
    monto,
    montoTotalPagosDisplay,
    montoTotalPagosLabel,
    calculatePreview: calculatePreview ?? null,
    isCalculating,
    saveValueToStore,
    setValid,
    paidAmount,
    invoiceTotal,
    isInvoiceFullyPaid,
    isAmountExceedingBalance,
  }
}

export default useAddPaymentComplementContainer
